import { ApiOutlined } from '@ant-design/icons';
import { Alert, Col, Form, Row, Segmented, Tag, TreeSelect } from 'antd';
import { useTranslation } from 'react-i18next';

import IntegrationCard from 'components/IntegrationCard/IntegrationCard';

import { KitCampaign } from 'models/KitCampaign';

import { FunctionsHelper } from 'utils/FunctionsHelper';

const I18N_BASE = 'scenes.settings.roles.components.kit_campaigns_fields';

type KitAccessMode = 'full' | 'limited';

interface KitCampaignsFieldsProps {
  campaigns: KitCampaign[];
  fullAccess: boolean;
  onFullAccessChange: (checked: boolean) => void;
  disabled?: boolean;
}

function KitCampaignsFields({
  campaigns,
  fullAccess,
  onFullAccessChange,
  disabled = false,
}: KitCampaignsFieldsProps) {
  const { t } = useTranslation();
  const form = Form.useFormInstance();
  const selectedCampaignIds = Form.useWatch<number[]>('campaignIds', form);

  const selectedCount = (selectedCampaignIds ?? []).filter(
    (v) => typeof v === 'number'
  ).length;

  const summary = fullAccess ? (
    <Tag color="green">{t(`${I18N_BASE}.summary.full_access`)}</Tag>
  ) : selectedCount > 0 ? (
    <Tag color="blue">
      {t(`${I18N_BASE}.summary.campaigns`, { count: selectedCount })}
    </Tag>
  ) : (
    <Tag>{t(`${I18N_BASE}.summary.empty`)}</Tag>
  );

  const treeData = Object.values(
    campaigns.reduce(
      (acc, c) => {
        const key = `company-${c.company.id}`;
        if (!acc[key]) {
          acc[key] = { title: c.company.name, value: key, children: [] };
        }
        acc[key].children.push({ title: c.name, value: c.id });
        return acc;
      },
      {} as Record<
        string,
        {
          title: string;
          value: string;
          children: { title: string; value: number }[];
        }
      >
    )
  ).map((node) => ({
    ...node,
    title: `${node.title} (${node.children.length})`,
  }));

  return (
    <IntegrationCard
      title={t(`${I18N_BASE}.title`)}
      icon={<ApiOutlined />}
      summary={summary}
    >
      <Row gutter={24}>
        <Col xs={24}>
          <Form.Item
            label={t(`${I18N_BASE}.labels.access`)}
            style={{ marginBottom: 16 }}
          >
            <Segmented<KitAccessMode>
              block
              value={fullAccess ? 'full' : 'limited'}
              onChange={(mode) => onFullAccessChange(mode === 'full')}
              disabled={disabled}
              options={[
                { value: 'full', label: t(`${I18N_BASE}.access.full`) },
                { value: 'limited', label: t(`${I18N_BASE}.access.limited`) },
              ]}
            />
          </Form.Item>
        </Col>
      </Row>

      {fullAccess && (
        <Alert
          type="info"
          showIcon
          message={t(`${I18N_BASE}.alerts.full_access`)}
        />
      )}

      {!fullAccess && (
        <Row gutter={24}>
          <Col xs={24}>
            <Form.Item
              name="campaignIds"
              label={t(`${I18N_BASE}.labels.campaigns`)}
              style={{ marginBottom: 0 }}
            >
              <TreeSelect
                treeData={treeData}
                treeCheckable
                showCheckedStrategy={TreeSelect.SHOW_CHILD}
                showSearch
                filterTreeNode={(search, node) =>
                  FunctionsHelper.normalizeText(
                    String(node.title ?? '')
                  ).includes(FunctionsHelper.normalizeText(search))
                }
                allowClear
                disabled={disabled}
                placeholder={t(`${I18N_BASE}.placeholders.campaigns`)}
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>
        </Row>
      )}
    </IntegrationCard>
  );
}

export default KitCampaignsFields;
